/**
 * Runner-side cloud credential bundle resolution.
 *
 * The platform delivers per-cloud secret bundles env-keyed in the
 * `session_init` secrets map (contract keys live in
 * `CLOUD_SECRET_ENV_KEYS`, bridge). This module picks the bundle for the
 * session's effective cloud and materializes the Vertex service-account JSON
 * to a 0600 file so the raw blob never rides `AgentConfig.cloudSecrets`.
 */

import { mkdirSync, rmSync, writeFileSync } from "node:fs";
import { dirname, join, resolve } from "node:path";
import { CLOUD_SECRET_ENV_KEYS } from "@skaile/workspaces/bridge";

/** Contract key carrying the raw Vertex service-account JSON blob. */
const GCP_SA_JSON_KEY = "GOOGLE_APPLICATION_CREDENTIALS_JSON";

/** Session-workspace-relative path the SA JSON is materialized to. */
const GCP_SA_RELATIVE_PATH = join(".skaile", "gcp-sa.json");

/**
 * Pick the contract credential bundle for `cloud` out of the `session_init`
 * secrets map. Unrelated secrets (connector tokens, provider API keys) are
 * ignored; missing bundle keys are simply absent from the result.
 *
 * When the raw Vertex SA JSON is present it is written to
 * `<projectDir>/.skaile/gcp-sa.json` (mode 0600) and the returned bundle
 * carries `GOOGLE_APPLICATION_CREDENTIALS` (the absolute file path) instead —
 * the raw JSON never enters `cloudSecrets`. Values are secrets: never logged.
 *
 * @since 1.3.0
 */
export function resolveCloudSecretBundle(args: {
  cloud: string;
  secrets: Record<string, string | undefined> | undefined;
  projectDir: string;
}): { cloudSecrets: Record<string, string> } {
  const { cloud, secrets, projectDir } = args;
  const cloudSecrets: Record<string, string> = {};
  const bundleKeys = (CLOUD_SECRET_ENV_KEYS as Record<string, readonly string[]>)[cloud];
  if (!secrets || !bundleKeys) return { cloudSecrets };

  for (const key of bundleKeys) {
    if (key === GCP_SA_JSON_KEY) continue; // materialized below, never passed through
    const value = secrets[key];
    if (value) cloudSecrets[key] = value;
  }

  const saJson = secrets[GCP_SA_JSON_KEY];
  if (bundleKeys.includes(GCP_SA_JSON_KEY) && saJson) {
    const saPath = resolve(projectDir, GCP_SA_RELATIVE_PATH);
    mkdirSync(dirname(saPath), { recursive: true });
    // Remove any previous file first so the write always CREATES it with mode
    // 0600 — writeFileSync's mode is ignored on overwrite, and a chmod after a
    // truncating write would leave a brief wrong-permissions window.
    rmSync(saPath, { force: true });
    writeFileSync(saPath, saJson, { mode: 0o600 });
    cloudSecrets.GOOGLE_APPLICATION_CREDENTIALS = saPath;
  }

  return { cloudSecrets };
}

/**
 * Best-effort removal of credential files materialized by
 * {@link resolveCloudSecretBundle}. Safe to call when nothing was written.
 *
 * @since 1.3.0
 */
export function cleanupCloudCredentialFiles(projectDir: string): void {
  try {
    rmSync(resolve(projectDir, GCP_SA_RELATIVE_PATH), { force: true });
  } catch {
    /* best-effort — nothing to do if the file is already gone or locked */
  }
}
